/**
 * Which setup sections the onboarding panel shows, and in which order — the
 * rule that keeps the "Erste Schritte" list in sync with the left sidebar.
 *
 *  - VISIBILITY: a section whose sidebar module the event doesn't have (plugin
 *    off, or the role can't read it) is dropped. It would otherwise offer setup
 *    for something the user cannot open — e.g. "Ziele & KPIs" on an event
 *    without the GOALS plugin. The two halves of that gate are applied
 *    separately: the ROLE half always (it needs no event read), the PLUGIN half
 *    only once the event's plugins are known.
 *  - ORDER: sections follow their module's position in the sidebar, including
 *    the event's saved (drag-reordered) sidebarOrder. Sections sharing a module
 *    (the guest-list lanes) keep their stored relative order — the plan array
 *    order is the interview's priority.
 *  - EMAILS stays ahead of WORKFLOWS: automations bind to the mail templates
 *    the EMAILS build creates, and the row for a blocked WORKFLOWS literally
 *    says "build emails first". The two swap positions with each other; no
 *    third section moves.
 *  - WORKFLOWS drops out when the EMAILS row it depends on is not visible —
 *    otherwise its row demands a step the user has no way to reach.
 *
 * Pure logic, unit-tested directly.
 */
import type { Capability } from "@/types/Capability";
import type { Plugin } from "@/types/enums/Plugin";
import { isSectionOpen, type SetupPlanSection } from "@/types/SetupPlan";
import {
  canReadSidebarModule,
  compareSidebarModules,
  hasSidebarModulePlugin,
} from "@/components/EventSidebar/sidebarModules";
import {
  BUILDABLE,
  SECTION_EXTRA_CAPABILITY,
  SECTION_EXTRA_PLUGIN,
  SECTION_MODULE,
  SECTION_NEEDS_BOOKABLE_ITEM,
  isKnownSectionKey,
} from "./sectionModules";

export interface VisibleSectionsInput {
  /**
   * The event's plugins. `undefined` = not loaded yet (or the request failed):
   * the plugin half of the gate is then skipped, so a slow/broken event read
   * hides nobody's setup work. The capability half still applies.
   */
  plugins?: Plugin[];
  can: (cap: Capability) => boolean;
  /** The event's saved sidebar order, when it has one. */
  sidebarOrder?: string[];
  /**
   * Whether the event's programme has at least one bookable (OFFERING) item —
   * see SECTION_NEEDS_BOOKABLE_ITEM. `undefined` (not read) counts as no: the
   * row it gates is opt-in content, not the user's pending work.
   */
  hasBookableProgramItem?: boolean;
}

/** Sections of the plan whose module this event + role has (any status). */
export function sectionsWithVisibleModule(
  sections: readonly SetupPlanSection[],
  { plugins, can, hasBookableProgramItem }: VisibleSectionsInput,
): SetupPlanSection[] {
  // A key may appear only once: the row list uses `key` as its React key, and
  // both the pair rule and the swap index by first match.
  const seen = new Set<string>();
  return sections.filter((section) => {
    if (!isKnownSectionKey(section.key)) return false;
    if (seen.has(section.key)) return false;
    seen.add(section.key);
    const module = SECTION_MODULE[section.key];

    if (!canReadSidebarModule(module, can)) return false;
    const extraCap = SECTION_EXTRA_CAPABILITY[section.key];
    if (extraCap && !can(extraCap)) return false;

    if (!plugins) return true; // event read unresolved → plugin half deferred
    if (!hasSidebarModulePlugin(module, plugins)) return false;
    const extraPlugin = SECTION_EXTRA_PLUGIN[section.key];
    if (extraPlugin && !plugins.includes(extraPlugin)) return false;
    return (
      !SECTION_NEEDS_BOOKABLE_ITEM.has(section.key) ||
      !!section.proposal?.trim() ||
      hasBookableProgramItem === true
    );
  });
}

/**
 * The rows the panel renders: open sections whose module the sidebar shows,
 * ordered like the sidebar. Takes the WHOLE plan — the WORKFLOWS/EMAILS rule
 * needs to see EMAILS even when it is already resolved.
 */
export function visibleSetupSections(
  sections: readonly SetupPlanSection[],
  input: VisibleSectionsInput,
): SetupPlanSection[] {
  const withModule = sectionsWithVisibleModule(sections, input);
  // The EMAILS ROW, not the emails module: a section the user skipped with the
  // ✕ is gone from the list even though `messaging` is right there in the
  // sidebar. Measuring the module would leave exactly the dead end this rule
  // exists to prevent.
  const emailsRowVisible = withModule.some(
    (s) => s.key === "EMAILS" && isSectionOpen(s.status),
  );

  const open = withModule
    .filter((section) => isSectionOpen(section.status))
    .filter(
      (section) =>
        section.key !== "WORKFLOWS" ||
        emailsRowVisible ||
        !workflowsBlocksOnEmails(section, sections),
    );

  // Stable sort: equal modules keep their stored (priority) order.
  const ordered = [...open].sort((a, b) =>
    compareSidebarModules(SECTION_MODULE[a.key], SECTION_MODULE[b.key], input.sidebarOrder),
  );

  return buildableFirst(ordered);
}

/**
 * Does this WORKFLOWS section depend on an EMAILS build that hasn't happened?
 * Its automations bind to the mail templates the EMAILS build creates, so the
 * row renders "build the emails first" and disables its build button.
 *
 * THE one definition: the panel row (OnboardingSetupTrack) renders the hint
 * from it, and visibleSetupSections drops the row entirely when the EMAILS row
 * it points at isn't on screen — a demand the user has no way to satisfy.
 * Two copies of this predicate drifted apart once already.
 */
export function workflowsBlocksOnEmails(
  workflows: SetupPlanSection | undefined,
  sections: readonly SetupPlanSection[],
): boolean {
  // No proposal = no emailKeys to bind = no dependency at all.
  if (!workflows?.proposal?.trim()) return false;
  const emails = sections.find((s) => s.key === "EMAILS");
  if (!emails) return false; // nothing to depend on — WORKFLOWS stands alone
  const emailsStillBuildable = !!emails.proposal?.trim() && emails.status !== "DONE_MANUALLY";
  return emailsStillBuildable && emails.status !== "BUILT";
}

/**
 * Buildable rows to the front, everything else behind them — each block keeps
 * the sidebar order it came in with. The panel scrolls at ~8 rows, and the
 * buildable sections are the ones carrying the content the interview prepared;
 * strict sidebar order parks messaging/automation last, out of sight.
 */
function buildableFirst(sections: SetupPlanSection[]): SetupPlanSection[] {
  const buildable = sections.filter((s) => BUILDABLE.has(s.key));
  const rest = sections.filter((s) => !BUILDABLE.has(s.key));
  return [...swapEmailsBeforeWorkflows(buildable), ...rest];
}

/**
 * Exchange the two rows' slots when the order put WORKFLOWS first — automations
 * bind to the templates the EMAILS build creates (see workflowsBlocksOnEmails).
 * A swap, not a move: no third row changes position.
 */
function swapEmailsBeforeWorkflows(sections: SetupPlanSection[]): SetupPlanSection[] {
  const emailsIndex = sections.findIndex((s) => s.key === "EMAILS");
  const workflowsIndex = sections.findIndex((s) => s.key === "WORKFLOWS");
  if (emailsIndex === -1 || workflowsIndex === -1 || emailsIndex < workflowsIndex) {
    return sections;
  }
  const result = [...sections];
  result[workflowsIndex] = sections[emailsIndex];
  result[emailsIndex] = sections[workflowsIndex];
  return result;
}
